/**
 * useWorkflow Hook
 *
 * React Query hooks for investment workflow operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  workflowService,
  SubmitForApprovalDto,
  CreateCommentDto,
  UpdateCommentDto,
  StatusChangeDto,
  DecisionsResponse,
} from '../services/workflow.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

import type {
  StatusHistory,
  InvestmentDecision,
  InvestmentComment,
  CreateInvestmentDecisionDto,
  CreateInvestmentDecisionResponseDto,
} from '@medsuite/models';

export const workflowKeys = {
  all: ['workflow'] as const,
  statusHistory: (investmentId: number) =>
    [...workflowKeys.all, 'status-history', investmentId] as const,
  decisions: (investmentId: number) => [...workflowKeys.all, 'decisions', investmentId] as const,
  latestDecision: (investmentId: number) =>
    [...workflowKeys.all, 'decisions', investmentId, 'latest'] as const,
  comments: (investmentId: number) => [...workflowKeys.all, 'comments', investmentId] as const,
};

/**
 * Fetch status history for an investment
 */
export function useStatusHistory(
  investmentId: number,
  options?: Omit<UseQueryOptions<StatusHistory[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<StatusHistory[], Error>({
    queryKey: workflowKeys.statusHistory(investmentId),
    queryFn: async () => {
      const response = await workflowService.getStatusHistory(investmentId);
      return response.data;
    },
    ...options,
  });
}

/**
 * Fetch decisions for an investment
 */
export function useDecisions(
  investmentId: number,
  options?: Omit<UseQueryOptions<DecisionsResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<DecisionsResponse, Error>({
    queryKey: workflowKeys.decisions(investmentId),
    queryFn: async () => {
      const response = await workflowService.getDecisions(investmentId);
      // Extract data from success wrapper
      return unwrapResponse<DecisionsResponse>(response.data);
    },
    ...options,
  });
}

/**
 * Fetch latest decision for an investment
 */
export function useLatestDecision(
  investmentId: number,
  options?: Omit<UseQueryOptions<InvestmentDecision | null, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentDecision | null, Error>({
    queryKey: workflowKeys.latestDecision(investmentId),
    queryFn: async () => {
      const response = await workflowService.getLatestDecision(investmentId);
      // Extract data from success wrapper
      const responseData = unwrapResponse<{ decision: InvestmentDecision | null }>(response.data);
      return responseData.decision;
    },
    ...options,
  });
}

/**
 * Fetch comments for an investment
 */
export function useComments(
  investmentId: number,
  options?: Omit<UseQueryOptions<InvestmentComment[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentComment[], Error>({
    queryKey: workflowKeys.comments(investmentId),
    queryFn: async () => {
      const response = await workflowService.getComments(investmentId);
      return response.data;
    },
    ...options,
  });
}

/**
 * Submit investment for approval
 */
export function useSubmitForApproval(
  investmentId: number,
  options?: UseMutationOptions<StatusHistory, Error, SubmitForApprovalDto | undefined>
) {
  const queryClient = useQueryClient();

  return useMutation<StatusHistory, Error, SubmitForApprovalDto | undefined>({
    mutationFn: async (data) => {
      const response = await workflowService.submitForApproval(investmentId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: investmentKeys.detail(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: investmentKeys.queue() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.statusHistory(investmentId) });
    },
    ...options,
  });
}

/**
 * Create investment decision (requires DecisionMaker role)
 *
 * Creates a decision record and updates the investment status.
 */
export function useCreateDecision(
  investmentId: number,
  options?: UseMutationOptions<
    CreateInvestmentDecisionResponseDto,
    Error,
    CreateInvestmentDecisionDto
  >
) {
  const queryClient = useQueryClient();

  return useMutation<CreateInvestmentDecisionResponseDto, Error, CreateInvestmentDecisionDto>({
    mutationFn: async (data) => {
      const response = await workflowService.createDecision(investmentId, data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate investment queries to refresh status
      queryClient.invalidateQueries({ queryKey: investmentKeys.detail(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: investmentKeys.queue() });
      // Invalidate workflow queries
      queryClient.invalidateQueries({ queryKey: workflowKeys.statusHistory(investmentId) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.decisions(investmentId) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.latestDecision(investmentId) });
    },
    ...options,
  });
}

/**
 * Change investment status
 */
export function useChangeStatus(
  investmentId: number,
  options?: UseMutationOptions<StatusHistory, Error, StatusChangeDto>
) {
  const queryClient = useQueryClient();

  return useMutation<StatusHistory, Error, StatusChangeDto>({
    mutationFn: async (data) => {
      const response = await workflowService.changeStatus(investmentId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: investmentKeys.detail(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.statusHistory(investmentId) });
    },
    ...options,
  });
}

/**
 * Add comment
 */
export function useAddComment(
  investmentId: number,
  options?: UseMutationOptions<InvestmentComment, Error, Omit<CreateCommentDto, 'investment_id'>>
) {
  const queryClient = useQueryClient();

  return useMutation<InvestmentComment, Error, Omit<CreateCommentDto, 'investment_id'>>({
    mutationFn: async (data) => {
      const response = await workflowService.addComment(investmentId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.comments(investmentId) });
    },
    ...options,
  });
}

/**
 * Update comment
 */
export function useUpdateComment(
  investmentId: number,
  options?: UseMutationOptions<
    InvestmentComment,
    Error,
    { commentId: number; data: UpdateCommentDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<InvestmentComment, Error, { commentId: number; data: UpdateCommentDto }>({
    mutationFn: async ({ commentId, data }) => {
      const response = await workflowService.updateComment(investmentId, commentId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.comments(investmentId) });
    },
    ...options,
  });
}

/**
 * Delete comment
 */
export function useDeleteComment(
  investmentId: number,
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (commentId) => {
      await workflowService.deleteComment(investmentId, commentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.comments(investmentId) });
    },
    ...options,
  });
}
